import { useMemo } from "react";
import { Timer, PlayCircle, MapPin, Navigation, Zap, Hash } from "lucide-react";
import { eventsForRow, formatHM, displayOffsetLabel, isFromDal } from "@/lib/scheduleTime";

/**
 * Palet warna per-tone. Setiap tone menyediakan warna text utama, aksen glow,
 * dan warna pill. Membedakan visual secara jelas antar jenis event.
 */
const TONE_STYLES = {
  danger: {
    time: "text-[#CA2047]",
    glow: "bg-[#CA2047]/30",
    ring: "ring-[#CA2047]/40",
    pill: "bg-[#CA2047]/15 text-[#ff6b8a] border-[#CA2047]/50",
    accent: "bg-[#CA2047]",
    shadow: "shadow-[0_0_60px_-15px_#CA2047]",
  },
  warn: {
    time: "text-[#FFFF00]",
    glow: "bg-[#FFFF00]/20",
    ring: "ring-[#FFFF00]/40",
    pill: "bg-[#FFFF00]/15 text-[#FFFF00] border-[#FFFF00]/50",
    accent: "bg-[#FFFF00]",
    shadow: "shadow-[0_0_60px_-15px_#FFFF00]",
  },
  dal: {
    time: "text-[#22d3ee]",
    glow: "bg-[#22d3ee]/25",
    ring: "ring-[#22d3ee]/40",
    pill: "bg-[#22d3ee]/15 text-[#22d3ee] border-[#22d3ee]/50",
    accent: "bg-[#22d3ee]",
    shadow: "shadow-[0_0_60px_-15px_#22d3ee]",
  },
  muted: {
    time: "text-slate-500",
    glow: "bg-slate-700/20",
    ring: "ring-slate-700/40",
    pill: "bg-slate-800 text-slate-400 border-slate-700",
    accent: "bg-slate-600",
    shadow: "",
  },
};

/**
 * upcoming: { kind: 'startup'|'departure', row, at: Date, secondsRemaining }
 * activeAlarm: same shape when firing
 */
export default function CountdownWidget({ upcoming, now, activeAlarm, planDate }) {
  const display = useMemo(() => {
    const effectiveTimeOf = (row) => {
      if (!row) return "";
      const { departureAt } = eventsForRow(row, now, planDate);
      return {
        time: formatHM(departureAt),
        offsetLabel: displayOffsetLabel(row),
      };
    };
    if (activeAlarm) {
      // DAL-origin: keberangkatan keluar depo → cyan tone.
      // Startup (T-30) → yellow, Departure → red.
      const alarmTone = isFromDal(activeAlarm.row)
        ? "dal"
        : activeAlarm.kind === "startup"
          ? "warn"
          : "danger";
      return {
        label:
          activeAlarm.kind === "startup"
            ? "T-30 STARTUP REMINDER"
            : "DEPARTURE NOW",
        time: "00:00",
        row: activeAlarm.row,
        tone: alarmTone,
        effective: effectiveTimeOf(activeAlarm.row),
        isActive: true,
        kind: activeAlarm.kind,
      };
    }
    if (!upcoming) {
      return {
        label: "TIDAK ADA JADWAL BERIKUTNYA",
        time: "--:--",
        row: null,
        tone: "muted",
        isActive: false,
      };
    }
    const s = Math.max(0, Math.floor(upcoming.secondsRemaining));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    let time;
    if (h > 0) time = `${h}h ${String(m).padStart(2, "0")}m`;
    else time = `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;

    // Color follows event KIND: startup → yellow, departure → red.
    // Baris keberangkatan dari DAL depo pakai cyan (tanpa startup — hanya
    // event departure yang muncul dari alarm engine).
    const tone = isFromDal(upcoming.row)
      ? "dal"
      : upcoming.kind === "startup"
        ? "warn"
        : "danger";

    const eff = effectiveTimeOf(upcoming.row);
    const fromDal = isFromDal(upcoming.row);
    return {
      // - Startup event → "NEXT STARTUP REMINDER (T-30)"
      // - Departure keluar DAL depo (Pulang) → "NEXT ARRIVAL"
      //   (dari sudut jalur, kereta keluar depo = tiba di jalur/stasiun)
      // - Departure lainnya (kereta pulang ke DAL) → "NEXT DEPARTURE"
      label:
        upcoming.kind === "startup"
          ? "NEXT STARTUP REMINDER (T-30)"
          : fromDal
            ? "NEXT ARRIVAL"
            : "NEXT DEPARTURE",
      time,
      row: upcoming.row,
      tone,
      effective: eff,
      subtitle:
          upcoming.kind === "startup"
            ? `Startup untuk keberangkatan ${eff.time}${eff.offsetLabel ? ` (${eff.offsetLabel} dari PDF ${upcoming.row.start_time})` : ""}`
            : `Keberangkatan pukul ${eff.time}${eff.offsetLabel ? ` (${eff.offsetLabel} dari PDF ${upcoming.row.start_time})` : ""}`,
      isActive: false,
      kind: upcoming.kind,
    };
  }, [upcoming, activeAlarm, now, planDate]);

  const style = TONE_STYLES[display.tone] || TONE_STYLES.muted;
  const hasRow = !!display.row;
  const fromDal = hasRow && isFromDal(display.row);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 ${display.isActive ? "border-current animate-pulse" : "border-[#243a52]"} bg-gradient-to-br from-[#0d1f30] via-[#14283d] to-[#0a1929] ${style.shadow} transition-all duration-500`}
      data-testid="countdown-widget"
    >
      {/* Ambient glow orbs */}
      <div className={`absolute -top-24 -right-24 h-72 w-72 rounded-full ${style.glow} blur-3xl pointer-events-none animate-pulse-slow`} />
      <div className={`absolute -bottom-20 -left-20 h-64 w-64 rounded-full ${style.glow} blur-3xl pointer-events-none opacity-60`} />

      {/* Accent left bar */}
      <div className={`absolute left-0 top-0 h-full w-1.5 ${style.accent}`} />

      <div className="relative p-5 md:p-6 lg:p-7">
        {/* Header: label + trainset */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center ring-2 ${style.ring} ${style.glow}`}>
              {hasRow ? (
                <PlayCircle className={`h-4 w-4 ${style.time}`} />
              ) : (
                <Timer className="h-4 w-4 text-slate-500" />
              )}
            </div>
            <div>
              <div className={`text-[11px] md:text-xs font-bold uppercase tracking-[0.28em] ${style.time}`}>
                {display.label}
              </div>
              {display.subtitle && (
                <div className="mt-1 text-[11px] md:text-xs text-slate-400 max-w-md">
                  {display.subtitle}
                </div>
              )}
            </div>
          </div>

          {hasRow && (
            <div className={`text-right px-3 py-2 rounded-xl border ${style.pill} min-w-[90px]`}>
              <div className="text-[9px] font-bold uppercase tracking-widest opacity-70 mb-0.5 flex items-center justify-end gap-1">
                <Hash className="h-2.5 w-2.5" />
                Trainset
              </div>
              <div className="font-mono font-black tracking-tight" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)", lineHeight: 1 }}>
                {display.row.trainset_id}
              </div>
            </div>
          )}
        </div>

        {/* Countdown time — the hero */}
        <div className="relative py-2 mb-4">
          <div
            className={`font-mono font-black leading-none tracking-tighter ${style.time} drop-shadow-lg`}
            style={{
              fontSize: "clamp(3rem, 8vw, 7rem)",
              textShadow: "0 0 30px currentColor",
            }}
            data-testid="countdown-value"
          >
            {display.time}
          </div>
        </div>

        {/* Info grid — bigger, more prominent */}
        {hasRow && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            <HeroField
              icon={<MapPin className="h-3.5 w-3.5" />}
              label="From"
              value={display.row.track_origin || "—"}
              accent={fromDal ? "text-[#22d3ee]" : "text-white"}
              accentBg={fromDal ? "bg-[#22d3ee]/10 border-[#22d3ee]/30" : "bg-[#0a1929]/60 border-[#243a52]"}
            />
            <HeroField
              icon={<Navigation className="h-3.5 w-3.5" />}
              label="To"
              value={display.row.track_destination || "—"}
              accent="text-white"
              accentBg="bg-[#0a1929]/60 border-[#243a52]"
            />
            <HeroField
              icon={<Zap className="h-3.5 w-3.5" />}
              label="Departure"
              value={display.effective?.time || display.row.start_time}
              mono
              accent={style.time}
              accentBg="bg-[#0a1929]/60 border-[#243a52]"
              highlight
            />
            <HeroField
              icon={<Hash className="h-3.5 w-3.5" />}
              label="Operation"
              value={display.row.operation_id || "—"}
              mono
              accent="text-white"
              accentBg="bg-[#0a1929]/60 border-[#243a52]"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function HeroField({ icon, label, value, mono, accent = "text-white", accentBg = "", highlight = false }) {
  return (
    <div
      className={`rounded-lg border p-2.5 md:p-3 backdrop-blur-sm ${accentBg} ${highlight ? "ring-1 ring-current" : ""}`}
    >
      <div className="flex items-center gap-1.5 text-[9px] md:text-[10px] uppercase tracking-widest text-slate-400 mb-1">
        <span className={accent}>{icon}</span>
        <span>{label}</span>
      </div>
      <div
        className={`${mono ? "font-mono" : "font-display"} font-bold tracking-tight ${accent}`}
        style={{ fontSize: "clamp(1.15rem, 1.9vw, 1.75rem)", lineHeight: 1.1 }}
      >
        {value}
      </div>
    </div>
  );
}
